import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { PageHeader } from '../components/Layout';
import { BarList, ErrorState, Kpi, Loading, TextInput } from '../components/ui';
import { api } from '../lib/api';
import { money, today } from '../lib/format';

interface ProfitAndLossReport {
  from: string;
  to: string;
  revenue: { fnb: number; rooms: number; total: number; bills: number };
  procurement: { total: number; count: number };
  expenses: {
    total: number;
    count: number;
    byCategory: Array<{ categoryId: string; category: string; amount: number; count: number }>;
  };
  payroll: {
    total: number;
    net: number;
    runs: Array<{ period: string; gross: number; net: number; headcount: number }>;
  };
  grossProfit: number;
  netProfit: number;
  byMonth: Array<{
    month: string;
    revenue: number;
    procurement: number;
    expenses: number;
    payroll: number;
    netProfit: number;
  }>;
}

const monthLabel = (ym: string): string => {
  const [year, month] = ym.split('-').map(Number);
  return new Date(year!, month! - 1, 1).toLocaleDateString('en-IN', {
    month: 'short',
    year: 'numeric',
  });
};

const firstOfMonth = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;

const QUICK_RANGES: Array<[string, () => { from: string; to: string }]> = [
  ['This month', () => ({ from: firstOfMonth(new Date()), to: today() })],
  [
    'Last month',
    () => {
      const now = new Date();
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return {
        from: firstOfMonth(lastMonthStart),
        to: `${lastMonthEnd.getFullYear()}-${String(lastMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(lastMonthEnd.getDate()).padStart(2, '0')}`,
      };
    },
  ],
  [
    'Last 3 months',
    () => ({ from: firstOfMonth(new Date(new Date().setMonth(new Date().getMonth() - 2))), to: today() }),
  ],
  ['This year', () => ({ from: `${new Date().getFullYear()}-01-01`, to: today() })],
];

export function ProfitAndLossPage() {
  const [from, setFrom] = useState(firstOfMonth(new Date()));
  const [to, setTo] = useState(today());

  const report = useQuery({
    queryKey: ['pnl', from, to],
    queryFn: () => api.get<ProfitAndLossReport>(`/api/reports/pnl?from=${from}&to=${to}`),
  });

  return (
    <div className="stack">
      <PageHeader
        title="Profit & Loss"
        subtitle="Revenue, procurement, expenses and payroll together"
      />

      <div className="card">
        <div className="card-body row gap-6" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextInput label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextInput label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          <div className="row gap-6" style={{ flexWrap: 'wrap' }}>
            {QUICK_RANGES.map(([label, range]) => (
              <button
                key={label}
                className="btn sm"
                onClick={() => {
                  const r = range();
                  setFrom(r.from);
                  setTo(r.to);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {report.isLoading ? (
        <Loading />
      ) : report.error ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <>
          <div className="kpis">
            <Kpi
              label="Revenue"
              value={money(report.data!.revenue.total)}
              foot={`F&B ${money(report.data!.revenue.fnb)} · Rooms ${money(report.data!.revenue.rooms)}`}
            />
            <Kpi
              label="Procurement"
              value={money(report.data!.procurement.total)}
              foot={`${report.data!.procurement.count} receipt(s)`}
              tone="amber"
            />
            <Kpi
              label="Operating expenses"
              value={money(report.data!.expenses.total)}
              foot={`${report.data!.expenses.count} entrie(s)`}
              tone="amber"
            />
            <Kpi
              label="Payroll"
              value={money(report.data!.payroll.total)}
              foot={`${report.data!.payroll.runs.length} pay run(s)`}
              tone="amber"
            />
            <Kpi
              label="Gross profit"
              value={money(report.data!.grossProfit)}
              foot="Revenue − procurement"
              tone={report.data!.grossProfit < 0 ? 'red' : 'blue'}
            />
            <Kpi
              label="Net profit"
              value={money(report.data!.netProfit)}
              foot="After expenses and payroll"
              tone={report.data!.netProfit < 0 ? 'red' : ''}
            />
          </div>

          <div className="card">
            <div className="card-body small muted">
              Procurement is what suppliers billed for stock received in this period, not the cost
              of what was actually used against sales (see Reports → Cost for food cost %). Payroll
              counts finalized pay runs only, for the whole calendar month even if the selected
              range only covers part of it - payroll doesn't run on partial months, so this shows the
              real figure rather than an estimate.
            </div>
          </div>

          {report.data!.byMonth.length > 1 ? (
            <div className="card">
              <div className="card-head">
                <h3>By month</h3>
              </div>
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th className="num">Revenue</th>
                      <th className="num">Procurement</th>
                      <th className="num">Expenses</th>
                      <th className="num">Payroll</th>
                      <th className="num">Net profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.data!.byMonth.map((row) => (
                      <tr key={row.month}>
                        <td>{monthLabel(row.month)}</td>
                        <td className="num">{money(row.revenue)}</td>
                        <td className="num">{money(row.procurement)}</td>
                        <td className="num">{money(row.expenses)}</td>
                        <td className="num">{money(row.payroll)}</td>
                        <td className="num b">{money(row.netProfit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="grid-2">
            <div className="card">
              <div className="card-head">
                <h3>Expenses by category</h3>
              </div>
              <div className="card-body">
                <BarList
                  rows={report.data!.expenses.byCategory.map((r) => ({
                    label: `${r.category} · ${r.count}`,
                    value: r.amount,
                  }))}
                  format={money}
                />
              </div>
            </div>
            <div className="card">
              <div className="card-head">
                <h3>Payroll runs</h3>
              </div>
              {report.data!.payroll.runs.length === 0 ? (
                <div className="card-body small muted">No finalized payroll in this period.</div>
              ) : (
                <div className="table-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th className="num">Headcount</th>
                        <th className="num">Gross</th>
                        <th className="num">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.data!.payroll.runs.map((run) => (
                        <tr key={run.period}>
                          <td>{monthLabel(run.period)}</td>
                          <td className="num">{run.headcount}</td>
                          <td className="num">{money(run.gross)}</td>
                          <td className="num">{money(run.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
